import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, MapPin, Clock, Truck, Info, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import OptimizedImage from '../components/OptimizedImage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allergyIcons = {
  contains_gluten: { label: 'G', fullLabel: 'Gluten', color: 'bg-amber-100 text-amber-700' },
  contains_dairy: { label: 'D', fullLabel: 'Dairy', color: 'bg-blue-100 text-blue-700' },
  contains_nuts: { label: 'N', fullLabel: 'Nuts', color: 'bg-orange-100 text-orange-700' },
  contains_eggs: { label: 'E', fullLabel: 'Eggs', color: 'bg-yellow-100 text-yellow-700' },
  contains_shellfish: { label: 'SF', fullLabel: 'Shellfish', color: 'bg-red-100 text-red-700' },
  contains_soy: { label: 'SY', fullLabel: 'Soy', color: 'bg-green-100 text-green-700' },
  vegetarian: { label: 'V', fullLabel: 'Vegetarian', color: 'bg-emerald-100 text-emerald-700' },
  vegan: { label: 'VE', fullLabel: 'Vegan', color: 'bg-lime-100 text-lime-700' },
  sesame: { label: 'S', fullLabel: 'Sesame', color: 'bg-pink-100 text-pink-700' },
};

const categoryOrder = [
  'Cold Entrees', 'Hot Entrees', 'Salad', 'Main Courses', 
  'Grills', 'Reva Steaks', 'Side', 'Breads', 'Rice', 'Desserts(P)'
];

const OrderPage = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAllergyInfo, setShowAllergyInfo] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { addToCart, orderDetails, setOrderDetails } = useCart();

  const fetchData = useCallback(async () => {
    try {
      const [menuRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/menu`),
        axios.get(`${API}/menu/categories`)
      ]);
      setMenuItems(menuRes.data);
      const sortedCats = categoriesRes.data.sort((a, b) => {
        const indexA = categoryOrder.indexOf(a.name);
        const indexB = categoryOrder.indexOf(b.name);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      setCategories(sortedCats);
    } catch (error) {
        console.error('Error fetching menu:', error);
        toast.error('Failed to load menu');
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && item.available;
  });

  const handleAddToCart = (item) => {
    addToCart(item);
    toast.success(`${item.name} added to cart`);
  };

  const getAllergies = (allergies) => {
    if (!allergies) return [];
    return Object.entries(allergies)
      .filter(([key, value]) => value && allergyIcons[key])
      .map(([key]) => ({ key, ...allergyIcons[key] }));
  };

  const getImageForItem = (item) => {
    if (item.image_url && !item.image_url.includes('unsplash.com')) {
      return item.image_url;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="order-page">
      {/* Hero Section */}
      <section className="relative pt-20 md:pt-24">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://images.unsplash.com/photo-1772479020020-9bd8d2f577d0"
            alt="Order Online"
            className="w-full h-full"
            width={1920}
            quality={75}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>
        
        <div className="relative z-10 px-4 sm:px-6 lg:px-12 py-16 md:py-20">
          <div className="max-w-2xl">
            <p className="text-malva-light font-sans text-xs sm:text-sm tracking-[0.25em] uppercase mb-3">
              Order Online
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight">
              Fine Dining, Delivered
            </h1>
            <p className="text-white/80 text-sm sm:text-base lg:text-lg max-w-lg">
              Experience our exceptional cuisine in the comfort of your home. 
              Available for collection and delivery across London.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content - Side by Side Layout */}
      <section className="px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            
            {/* Left Sidebar - Order Type, Categories & Allergens */}
            <aside className="w-full lg:w-64 xl:w-72 flex-shrink-0">
              {/* Mobile Toggle */}
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 mb-4"
              >
                <span className="font-medium text-gray-700">Order Options & Filters</span>
                <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
              </button>

              <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block space-y-4 lg:sticky lg:top-24`}>
                {/* Order Type Selection */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-serif text-lg text-gray-800 mb-4">Order Type</h3>
                  <Tabs defaultValue="collection" value={orderDetails.order_type}
                    onValueChange={(value) => setOrderDetails({ ...orderDetails, order_type: value })} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-lg h-auto">
                      <TabsTrigger value="collection" className="rounded-md py-2 text-xs data-[state=active]:bg-malva-dark data-[state=active]:text-white" data-testid="order-type-collection">
                        <MapPin className="w-3 h-3 mr-1" /> Collection
                      </TabsTrigger>
                      <TabsTrigger value="delivery" className="rounded-md py-2 text-xs data-[state=active]:bg-malva-dark data-[state=active]:text-white" data-testid="order-type-delivery">
                        <Truck className="w-3 h-3 mr-1" /> Delivery
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="collection" className="mt-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-malva-dark mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-gray-700 text-xs font-medium">Collection Address</p>
                            <p className="text-gray-500 text-xs mt-0.5">45-47 York St, Twickenham, TW1 3LP</p>
                            <div className="flex items-center gap-1 mt-1.5 text-gray-400 text-[10px]">
                              <Clock className="w-3 h-3" /> 
                              <span>Ready in 30-45 min</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="delivery" className="mt-3">
                      <p className="text-gray-500 text-xs mb-3">Order via our delivery partners:</p>
                      <div className="space-y-2">
                        <a 
                          href="https://www.just-eat.co.uk" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-center p-2.5 rounded-lg bg-[#FF8000] hover:bg-[#E67300] transition-all text-white text-xs font-bold"
                          data-testid="delivery-just-eat"
                        >
                          just eat
                        </a>
                        <a 
                          href="https://www.ubereats.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-center p-2.5 rounded-lg bg-black hover:bg-gray-900 transition-all text-white text-xs font-bold"
                          data-testid="delivery-uber-eats"
                        >
                          Uber <span className="text-[#06C167] ml-0.5">Eats</span>
                        </a>
                        <a 
                          href="https://deliveroo.co.uk" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center justify-center p-2.5 rounded-lg bg-[#00CCBC] hover:bg-[#00B5A6] transition-all text-white text-xs font-bold"
                          data-testid="delivery-deliveroo"
                        >
                          deliveroo
                        </a>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Search */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search dishes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-10 text-sm bg-gray-50 border-gray-200 focus:border-malva focus:ring-malva/20"
                      data-testid="order-search"
                    />
                  </div>
                </div>

                {/* Categories */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-serif text-lg text-gray-800 mb-4">Categories</h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        selectedCategory === 'all'
                          ? 'bg-malva-dark text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      data-testid="order-category-all"
                    >
                      <span>All Dishes</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        selectedCategory === 'all' ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {menuItems.filter(i => i.available).length}
                      </span>
                    </button>
                    {categories.map((cat) => {
                      const count = menuItems.filter(i => i.category === cat.name && i.available).length;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat.name)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                            selectedCategory === cat.name
                              ? 'bg-malva-dark text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                          data-testid={`order-category-${cat.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        >
                          <span>{cat.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            selectedCategory === cat.name ? 'bg-white/20' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Allergen Information */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => setShowAllergyInfo(!showAllergyInfo)}
                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    data-testid="allergy-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-malva-dark" />
                      <span className="font-serif text-lg text-gray-800">Allergen Guide</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showAllergyInfo ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showAllergyInfo && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(allergyIcons).map(([key, { label, fullLabel, color }]) => (
                          <div key={key} className="flex items-center gap-2 py-1.5">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${color}`}>
                              {label}
                            </span>
                            <span className="text-xs text-gray-600">{fullLabel}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </aside>

            {/* Right Content - Menu Items */}
            <main className="flex-1 min-w-0">
              {/* Results Header */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-gray-500 text-sm">
                  Showing <span className="font-semibold text-gray-700">{filteredItems.length}</span> {filteredItems.length === 1 ? 'dish' : 'dishes'}
                  {selectedCategory !== 'all' && (
                    <span> in <span className="text-malva-dark font-medium">{selectedCategory}</span></span>
                  )}
                </p>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {Array(6).fill(0).map((_, i) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden animate-pulse">
                      <div className="aspect-[4/3] bg-gray-100" />
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-gray-100 rounded w-3/4" />
                        <div className="h-3 bg-gray-100 rounded w-full" />
                        <div className="h-3 bg-gray-100 rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <Search className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg mb-1">No dishes found</p>
                  <p className="text-gray-400 text-sm">Try adjusting your search or category</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  <TooltipProvider>
                    {filteredItems.map((item) => {
                      const itemImage = getImageForItem(item);
                      const allergies = getAllergies(item.allergies);
                      
                      return (
                        <div 
                          key={item.id} 
                          className="bg-white rounded-xl border border-gray-100 overflow-hidden group hover:shadow-lg hover:border-malva/20 transition-all duration-300"
                          data-testid={`order-item-${item.id}`}
                        >
                          {/* Image */}
                          <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-malva/5 to-gray-100 relative">
                            {itemImage ? (
                              <OptimizedImage
                                src={itemImage}
                                alt={item.name}
                                className="w-full h-full group-hover:scale-105 transition-transform duration-500"
                                width={400}
                                quality={70}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="text-center">
                                  <div className="w-12 h-12 rounded-full bg-malva/15 flex items-center justify-center mx-auto mb-2">
                                    <span className="text-xl font-serif text-malva-dark">
                                      {item.name.charAt(0)}
                                    </span>
                                  </div>
                                  <p className="text-gray-400 text-xs">Image coming soon</p>
                                </div>
                              </div>
                            )}
                            {item.chef_choice && (
                              <div className="absolute top-2 left-2 bg-malva-dark text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                                Chef's Choice
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-4">
                            <p className="text-malva-dark text-[10px] font-medium tracking-wider uppercase mb-1">{item.category}</p>
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <h3 className="font-serif text-base sm:text-lg text-gray-800 leading-snug line-clamp-2">
                                {item.name}
                              </h3>
                              <span className="text-malva-dark font-serif text-base sm:text-lg font-medium whitespace-nowrap">
                                £{item.price.toFixed(2)}
                              </span>
                            </div>
                            
                            <p className="text-gray-500 text-xs sm:text-sm mb-3 line-clamp-2 leading-relaxed">
                              {item.description}
                            </p>

                            <div className="flex items-center justify-between gap-2">
                              {/* Allergy Badges */}
                              <div className="flex flex-wrap gap-1">
                                {allergies.slice(0, 3).map(({ key, label, color }) => (
                                  <Tooltip key={key}>
                                    <TooltipTrigger asChild>
                                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold cursor-help ${color}`}>
                                        {label}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">{allergyIcons[key]?.fullLabel}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                                {allergies.length > 3 && (
                                  <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-[10px]">
                                    +{allergies.length - 3}
                                  </span>
                                )}
                              </div>

                              {/* Add to Cart */}
                              <button
                                onClick={() => handleAddToCart(item)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-malva-dark hover:bg-malva-darker text-white text-xs sm:text-sm font-medium rounded-full transition-colors"
                                data-testid={`order-add-${item.id}`}
                              >
                                <Plus className="w-3.5 h-3.5" />
                                <span>Add</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </TooltipProvider>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrderPage;
